import { useEffect, useRef } from 'react';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import { EMAIL_INVALID, requiredMsg } from 'lib/constants/messages';

import Input from 'components/shared/Input/Input';
import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';

const CONTENT = {
  email: {
    title: 'Custom email address',
    label: 'Email address',
    placeholder: 'email@example.com'
  },
  phone: {
    title: 'Custom phone number',
    label: 'Phone number',
    placeholder: '+1(123) 456 7890'
  }
};

const validationSchema = Yup.object().shape({
  customContact: Yup.string().when('contactType', {
    is: 'email',
    then: () => Yup.string().email(EMAIL_INVALID).required(requiredMsg('Email')),
    otherwise: () =>
      Yup.string()
        .matches(/^\+?[0-9()\s-]{7,}$/, 'Enter a valid phone number')
        .required(requiredMsg('Phone number'))
  })
});

const CustomContactModal = ({ type, onClose, onSubmit, isLoading = false }) => {
  const lastTypeRef = useRef(type);
  if (type) lastTypeRef.current = type;
  const activeType = type || lastTypeRef.current || 'email';
  const content = CONTENT[activeType];

  const formik = useFormik({
    initialValues: { customContact: '', contactType: activeType },
    validationSchema,
    onSubmit: async ({ customContact }) => {
      await onSubmit(activeType, customContact);
      onClose();
    }
  });

  useEffect(() => {
    if (type) formik.resetForm({ values: { customContact: '', contactType: type } });
  }, [type]);

  return (
    <Modal
      className="xs:!rounded-none"
      isOpen={!!type}
      isCentered
      handleClose={onClose}
      bodyClassName="bg-white p-4"
      customTitle={
        <Typography variant="p" weight="semibold" color="neutral-800" size="small">
          {content.title}
        </Typography>
      }
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex w-full justify-end gap-2">
            <Button text="Cancel" onClick={onClose} style="outlined" color="neutral" size="small" />
            <Button
              text="Send"
              size="small"
              onClick={formik.handleSubmit}
              isLoading={isLoading}
              isDisabled={!formik.values.customContact || isLoading}
            />
          </div>
        </div>
      }
      overlayClassName="xs:!p-0">
      <Input
        label={content.label}
        value={formik.values.customContact}
        onChange={formik.handleChange}
        placeholder={content.placeholder}
        name="customContact"
        id="customContact"
        required
        error={formik.touched.customContact && formik.errors.customContact}
      />
    </Modal>
  );
};

export default CustomContactModal;
